import React, { useRef, useEffect, useState } from "react";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
  WhatsappIcon,
} from "react-share";

interface VideoPlayerProps {
  src: string;
  className?: string;
  title: string;
  description: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  title,
  description,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true); // Default to playing
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    setCurrentUrl(window.location.href);

    // Automatically play the video on page load
    if (videoRef.current) {
      videoRef.current.play();
    }
  }, []);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  return (
    <div
      className={`video-wrapper ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        loop // Ensure the video loops
        muted
        playsInline
        className="video-element"
        aria-label={`Video: ${title}. ${description}`}
      />
      {currentUrl && (
        <div className={`social-share ${isHovered ? "visible" : ""}`}>
          <FacebookShareButton
            url={currentUrl}
            quote={title}
            aria-label="Share on Facebook"
          >
            <FacebookIcon size={32} round />
          </FacebookShareButton>
          <TwitterShareButton
            url={currentUrl}
            title={title}
            aria-label="Share on Twitter"
          >
            <TwitterIcon size={32} round />
          </TwitterShareButton>
          <LinkedinShareButton
            url={currentUrl}
            title={title}
            aria-label="Share on LinkedIn"
          >
            <LinkedinIcon size={32} round />
          </LinkedinShareButton>
          <WhatsappShareButton
            url={currentUrl}
            title={title}
            aria-label="Share on WhatsApp"
          >
            <WhatsappIcon size={32} round />
          </WhatsappShareButton>
        </div>
      )}
      <style jsx>{`
        .video-wrapper {
          position: relative;
          width: 100%;
          max-width: 100%;
          height: auto;
          aspect-ratio: 16 / 9;
          overflow: hidden;
          background-color: transparent;
        }
        .video-element {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: none;
        }
        .social-share {
          position: absolute;
          top: 10px;
          right: 10px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          opacity: 0;
          transition: opacity 0.3s;
        }
        .social-share.visible {
          opacity: 1;
        }
        @media (max-width: 767px) {
          .video-wrapper {
            aspect-ratio: auto;
            height: 0;
            padding-bottom: 56.25%;
          }
          .video-element {
            position: absolute;
            top: 0;
            left: 0;
          }
          .social-share {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoPlayer;
